import React, { FC } from 'react';
import {
  Button,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text
} from '@chakra-ui/react';
import { useContract, useSwitchChain } from '@thirdweb-dev/react';
import { Position } from '@cryptoalgebra/kim-sdk';
import { algebraPositionManagerABI } from '../../../../../abis/v3';
import { useAsyncFn } from 'react-use';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { useUserState } from '@app/state/userStore';
import { SubmitButton } from '@app/components/SubmitButton';
import { getActiveChain } from '@app/constants/chains';
import { useConfig } from '@app/config';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

interface Props {
  onClose: (txHash?: string) => void;
  isOpen: boolean;
  position: Position;
  positionId: number;
}

export const BurnLiquidityModalV3Content: FC<Props> = ({
  isOpen,
  onClose,
  position,
  positionId
}) => {
  const switchChain = useSwitchChain();
  const { txDeadline } = useUserState();
  const config = useConfig();
  const isNetworkMismatch = useNetworkMismatch();

  const { contract } = useContract(
    config?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER,
    algebraPositionManagerABI
  );

  const [{ loading: isLoading }, add] = useAsyncFn(async () => {
    if (!positionId || !contract) {
      return;
    }

    try {
      const res = await contract.call('burn', [positionId.toString()]);

      if (res?.reason) {
        showErrorToast('Transaction rejected');
      }

      const txHash = res?.receipt?.transactionHash;

      if (txHash) {
        showSuccessToast(
          `Transaction successful.`,
          <ExternalLink txHash={txHash} />
        );
        onClose(txHash);
      }
    } catch (e: any) {
      console.error(e.message);

      if (e.message?.includes('user rejected transaction')) {
        return;
      }

      showErrorToast(`Transaction failed: ${e.message}`);
    }
  }, [contract, positionId, txDeadline]);

  function renderSubmit() {
    if (isNetworkMismatch) {
      return (
        <SubmitButton
          mt={0}
          label="Switch chain"
          isLoading={false}
          variant={'destructive'}
          onClick={() => switchChain(getActiveChain().chainId)}
        />
      );
    }

    return (
      <SubmitButton
        mt={0}
        label="Burn"
        isLoading={isLoading}
        isDisabled={!position || isLoading}
        onClick={() => add && add()}
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <ModalHeader>
          <HStack justify="space-between" px={0} py={0} mb="16px">
            <Text fontSize="16px" color="white" fontWeight="500">
              Burn position
            </Text>
            <ModalCloseButton onClick={() => onClose()} />
          </HStack>
        </ModalHeader>
        <Text fontSize="14px" color="neutral.300" fontWeight="400">
          Are you sure you want to burn position?
        </Text>
        <HStack w="100%" mt={6}>
          <Button variant="secondary" onClick={() => onClose()} w="100%">
            Cancel
          </Button>
          {renderSubmit()}
        </HStack>
      </ModalContent>
    </Modal>
  );
};
